import React, { useEffect, useState } from "react";
import "./AdminModal.css";
import { AdminUser, CmsRole } from "../../../../apis/adminApis";
import Select from "../../../../components/reusable-select/Select";
import InputBox from "../../../../components/reusable-input/InputBox";
import Button from "../../../../components/reusable-button/Button";
import Switch from "../../../../components/reusable-switch/Switch";
import { formatName } from "../../../../utils/formatName";
import { FiCopy, FiCheck } from "react-icons/fi";

const CopyIcon = FiCopy as unknown as React.FC<React.SVGProps<SVGSVGElement>>;
const CheckIcon = FiCheck as unknown as React.FC<React.SVGProps<SVGSVGElement>>;

interface Props {
  open: boolean;
  onClose: () => void;
  onSubmit: (
    formData: Omit<AdminUser, "id" | "createdAt" | "updatedAt"> & {
      password?: string;
      roleId: string;
    }
  ) => void;
  initialData?: AdminUser | null;
  roles: CmsRole[];
  loading?: boolean;
}

const AdminModal: React.FC<Props> = ({
  open,
  onClose,
  onSubmit,
  initialData,
  roles,
  loading,
}) => {
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [error, setError] = useState("");
  const [formData, setFormData] = useState<
    Omit<AdminUser, "id" | "createdAt" | "updatedAt"> & { roleId: string }
  >({
    firstName: "",
    middleName: "",
    lastName: "",
    email: "",
    designation: "",
    isActive: true,
    roleId: "",
    roleIds: {
      roleName: "",
    },
  });

  const [showPassword, setShowPassword] = useState(false);
  const [copiedPassword, setCopiedPassword] = useState(false);

  useEffect(() => {
    if (!roles.length) return;

    if (initialData) {
      const { id, createdAt, updatedAt, ...rest } = initialData;
      setFormData({ ...rest, roleId: initialData.roleId || "" });
      setPassword("");
    } else {
      setFormData({
        firstName: "",
        middleName: "",
        lastName: "",
        email: "",
        designation: "",
        isActive: true,
        roleId: "",
        roleIds: {
          roleName: "",
        },
      });
      setPassword("");
    }
  }, [initialData, roles]);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: name === "isActive" ? value === "true" : value,
    }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    if (!formData.roleId) {
      setError("Please select a role.");
      return;
    }

    if (!initialData) {
      if (password !== confirmPassword) {
        setError("Passwords do not match.");
        return;
      }
    }

    onSubmit({
      ...formData,
      ...(initialData ? {} : { password }),
    });
  };

  if (!open) return null;

  return (
    <div className="modal-overlay">
      <div className="modal-box">
        <div className="admin-modal-title">
          {initialData ? "Edit Admin" : "Add Admin"}
        </div>
        <form onSubmit={handleSubmit}>
          <InputBox
            label="First Name"
            name="firstName"
            placeholder="First Name"
            value={formData.firstName}
            onChange={handleChange}
            required
            maxLength={60}
          />

          <InputBox
            label="Middle Name"
            name="middleName"
            placeholder="Middle Name"
            value={formData.middleName}
            onChange={handleChange}
            maxLength={60}
          />

          <InputBox
            label="Last Name"
            name="lastName"
            placeholder="Last Name"
            value={formData.lastName}
            onChange={handleChange}
            required
            maxLength={60}
          />

          <InputBox
            label="Email"
            name="email"
            placeholder="Email"
            value={formData.email}
            onChange={handleChange}
            required
            type="email"
            maxLength={120}
            disabled={initialData ? true : false} // disable if editing
          />

          {!initialData && (
            <>
              <div className="password-input-wrapper">
                <div style={{ width: "98%" }}>
                  <InputBox
                    label="Password"
                    name="password"
                    placeholder="Password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    type="password"
                    showPasswordToggle
                    showPassword={showPassword}
                    onTogglePassword={() => setShowPassword((prev) => !prev)}
                    maxLength={120}
                  />
                </div>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    marginTop: "35px",
                  }}
                >
                  {copiedPassword ? (
                    <CheckIcon
                      className="copy-icon"
                      style={{
                        opacity: password ? 1 : 0.4,
                        cursor: password ? "pointer" : "not-allowed",
                      }}
                    />
                  ) : (
                    <CopyIcon
                      className="copy-icon"
                      onClick={
                        password
                          ? () => {
                              navigator.clipboard.writeText(password);
                              setCopiedPassword(true);
                              setTimeout(() => setCopiedPassword(false), 2000);
                            }
                          : undefined
                      }
                      style={{
                        opacity: password ? 1 : 0.4,
                        cursor: password ? "pointer" : "not-allowed",
                      }}
                    />
                  )}
                </div>
              </div>

              <InputBox
                label="Confirm Password"
                name="confirmPassword"
                placeholder="Confirm Password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
                type="password"
                showPasswordToggle
                showPassword={showPassword}
                onTogglePassword={() => setShowPassword((prev) => !prev)}
                maxLength={120}
              />
            </>
          )}

          <InputBox
            label="Designation"
            name="designation"
            placeholder="Designation"
            value={formData.designation}
            onChange={handleChange}
            required
            maxLength={120}
          />

          <Select
            label="Role"
            name="roleId"
            disabledValue="Select a Role"
            value={formData.roleId}
            onChange={handleChange}
            options={roles.map((role) => ({
              label: formatName(role.roleName),
              value: role.id,
            }))}
            required
          />
          {initialData && (
            <Switch
              label={formData.isActive ? "Active" : "Inactive"}
              checked={formData.isActive}
              onChange={() =>
                setFormData((prev) => ({ ...prev, isActive: !prev.isActive }))
              }
            />
          )}

          {error && <div className="admin-error-message">{error}</div>}

          <div className="admin-modal-actions">
            <Button
              label="Cancel"
              type="button"
              variant="outlined"
              onClick={onClose}
            />

            <Button
              label={initialData ? "Update" : "Create"}
              type="submit"
              variant="solid"
              loading={loading}
            />
          </div>
        </form>
      </div>
    </div>
  );
};

export default AdminModal;
